import {
  Box,
  TextField,
  TextFieldProps,
  Grid,
  Switch,
  IconButton,
} from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { Dimension, Units, toUnits, convert } from "./util";
import interpolate from "../../util/interpolate";
import { IDimensionsTranslations } from "../../types";

const StyledTextField = styled((props: TextFieldProps) => (
  <TextField {...props} />
))(({ theme }) => ({
  width: "110px",

  "& .MuiFormHelperText-root": {
    margin: 4,
    marginRight: 0,
    fontSize: 10,

    [theme.breakpoints.down("md")]: {
      fontSize: 9,
    },
  },

  [theme.breakpoints.down("md")]: {
    width: "100px",
  },
}));

const graphicAsset = (path: string) => {
  return `https://graphic-assets.fra1.cdn.digitaloceanspaces.com/${path}`;
};

export interface DimensionsProps {
  width: number;
  height: number;
  units: Units;
  showInches?: boolean;

  displayAs: Units;

  minDimensions: Dimension;
  maxDimensions: Dimension;

  onChangeConfirmed: (
    newWidth: number,
    newHeight: number,
    newUnit: Units
  ) => void;
  onError?: (hasError: boolean) => void;

  translations: IDimensionsTranslations;
  fixedAspectRatio?: boolean;
}

export const Dimensions: FC<DimensionsProps> = (props) => {
  const { units } = props;

  const [displayAs, setDisplayAs] = useState(props.displayAs || "cm");
  const [width, setWidth] = useState(props.width.toString());
  const [height, setHeight] = useState(props.height.toString());
  const widthNum = parseFloat(width);
  const heightNum = parseFloat(height);
  const [fixedAspectRatio, setFixedAspectRatio] = useState(
    props.fixedAspectRatio
  );
  const [originalAspectRatio, setOriginalAspectRatio] = useState(
    widthNum / heightNum
  );

  const widthTooSmall =
    widthNum <
    convert(props.minDimensions.unit, displayAs, props.minDimensions.width);
  const widthTooBig =
    widthNum >
    convert(props.maxDimensions.unit, displayAs, props.maxDimensions.width);

  const heightTooSmall =
    heightNum <
    convert(props.minDimensions.unit, displayAs, props.minDimensions.height);

  const heightTooBig =
    heightNum >
    convert(props.maxDimensions.unit, displayAs, props.maxDimensions.height);

  const isWidthMoreThan2Decimal = !width.match(/^\d*(\.\d{0,1})?$/);
  const isHeightMoreThan2Decimal = !height.match(/^\d*(\.\d{0,1})?$/);

  const errors =
    widthTooSmall ||
    widthTooBig ||
    heightTooSmall ||
    heightTooBig ||
    isWidthMoreThan2Decimal ||
    isHeightMoreThan2Decimal;

  useEffect(() => {
    if (errors) {
      return;
    }

    props.onChangeConfirmed(widthNum, heightNum, displayAs);
  }, [errors, widthNum, heightNum, displayAs]);

  useEffect(() => {
    if (props.onError) {
      props.onError(Object.values(errors).some((v) => v));
    }
  }, [errors]);

  const tr = interpolate(props.translations);

  const t = (k: keyof IDimensionsTranslations) => {
    const v = tr(k, {
      units:
        displayAs === "cm"
          ? tr("cm")
          : displayAs === "mm"
          ? tr("mm")
          : tr("in"),
      width: toUnits(units, props.width),
      height: toUnits(units, props.height),
      minWidth: toUnits(
        displayAs,
        convert(props.minDimensions.unit, displayAs, props.minDimensions.width)
      ),
      maxWidth: toUnits(
        displayAs,
        convert(props.maxDimensions.unit, displayAs, props.maxDimensions.width)
      ),
      minHeight: toUnits(
        displayAs,
        convert(props.minDimensions.unit, displayAs, props.minDimensions.height)
      ),

      maxHeight: toUnits(
        displayAs,
        convert(props.maxDimensions.unit, displayAs, props.maxDimensions.height)
      ),
    });

    return v;
  };

  const unitsText = t(displayAs);

  const handleCmInch = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    const newUnits = ev.target.checked ? "in" : "cm";
    setWidth(toUnits(newUnits, convert(units, newUnits, widthNum)));
    setHeight(toUnits(newUnits, convert(units, newUnits, heightNum)));
    setDisplayAs(newUnits);
  };

  const handleSetWidth = (
    ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (fixedAspectRatio) {
      setHeight((parseFloat(ev.target.value) / originalAspectRatio).toFixed(1));
    }
    setWidth(ev.target.value);
  };

  const handleSetHeight = (
    ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (fixedAspectRatio) {
      setWidth((parseFloat(ev.target.value) * originalAspectRatio).toFixed(1));
    }
    setHeight(ev.target.value);
  };

  return (
    <Grid container alignItems="center" sx={{ position: "relative" }}>
      {props.fixedAspectRatio ? (
        <Grid item xs={2}>
          <IconButton
            sx={{ marginTop: -2 }}
            onClick={() => {
              setFixedAspectRatio((prev) => !prev);
              setOriginalAspectRatio(widthNum / heightNum);
            }}
          >
            <img
              height={24}
              width={24}
              src={graphicAsset(
                fixedAspectRatio ? "locked.svg" : "unlocked.svg"
              )}
            />
          </IconButton>
        </Grid>
      ) : null}
      <Grid item xs={4.3} md={4.5} marginRight={2}>
        <StyledTextField
          error={widthTooBig || widthTooSmall || isWidthMoreThan2Decimal}
          label={`${t("widthLabel")} (${unitsText})`}
          value={width}
          onChange={handleSetWidth}
          type="number"
          inputProps={{
            step: 0.1,
            min: convert(
              props.minDimensions.unit,
              displayAs,
              props.minDimensions.width
            ).toFixed(1),
            max: convert(
              props.maxDimensions.unit,
              displayAs,
              props.maxDimensions.width
            ).toFixed(1),
          }}
          helperText={
            isWidthMoreThan2Decimal
              ? t("maxdp")
              : widthTooSmall
              ? t("widthTooSmall")
              : widthTooBig
              ? t("widthTooBig")
              : t("widthHelperText")
          }
          role="width"
        />
      </Grid>
      <Grid item xs={4.3} md={4.5}>
        <StyledTextField
          error={heightTooBig || heightTooSmall || isHeightMoreThan2Decimal}
          label={`${t("heightLabel")} (${unitsText})`}
          value={height}
          onChange={handleSetHeight}
          type="number"
          inputProps={{
            step: 0.1,
            min: convert(
              props.minDimensions.unit,
              displayAs,
              props.minDimensions.width
            ).toFixed(1),
            max: convert(
              props.maxDimensions.unit,
              displayAs,
              props.maxDimensions.width
            ).toFixed(1),
          }}
          helperText={
            isHeightMoreThan2Decimal
              ? t("maxdp")
              : heightTooSmall
              ? t("heightTooSmall")
              : heightTooBig
              ? t("heightTooBig")
              : t("heightHelperText")
          }
          role="height"
        />
      </Grid>
      {props.showInches ? (
        <Box
          sx={{
            fontFamily: "Poppins",
            fontSize: "10px",
            display: "flex",
            alignItems: "center",
            position: "absolute",
            top: "20px",
            right: {
              xs: 0,
              md: "-5px",
            },
          }}
        >
          <div>cm</div>
          <Switch
            size="small"
            checked={units === "in"}
            onChange={handleCmInch}
            role="change-units"
          />
          <div>in</div>
        </Box>
      ) : null}
    </Grid>
  );
};
